import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { LoadingButton } from '@/components/loading-button'
import { SearchSelect } from '@/components/search-select'
import { InlineFieldInput } from '@/components/inline-field-group'
import { Separator } from '@/components/ui/separator'
import { toast } from 'sonner'
import {
  useE2EScenarios,
  useE2EStatus,
  useE2ESeed,
  useE2EInjectReply,
  useE2EInjectSpecCreditReply,
  useE2ESendPO,
  useE2EInjectCustomerReply,
  useE2EInjectShippingReply,
  useE2ECleanup,
} from '@/api/hooks'
import { FlaskConical, Play, Send, Trash2, ArrowRight, Check } from 'lucide-react'
import { getApiErrorMessage } from '@/lib/api-error'

interface SeededPO {
  uuid: string
  po_number: string
  manufacturer_name: string
  we_book_freight: boolean
  has_spec_credit: boolean
  po_thread_uuid?: string | null
  spec_credit_thread_uuid?: string | null
  shipping_profile?: string
  shipping_label?: string
  freight_note?: string
}

interface CustomerThreadResult {
  thread_uuid: string
  recipient_email: string
  recipient_name: string
}

interface SeedResult {
  project_uuid: string
  order_uuid: string
  purchase_orders: SeededPO[]
  customer_thread?: CustomerThreadResult
}

interface E2EStatus {
  projects: number
  purchase_orders: number
  communication_threads: number
  communication_messages: number
  customer_communication_threads: number
  shipping_communication_threads: number
}

interface E2EScenarios {
  manufacturer?: Record<string, { category: string; description: string }>
  spec_credit?: Record<string, { category: string; description: string }>
  customer?: Record<string, { category: string; description: string }>
  shipping?: Record<string, { category: string; description: string }>
}

function scenarioOptions(group: Record<string, { description: string }> | undefined) {
  return Object.entries(group ?? {}).map(([key, s]) => ({ value: key, label: key, description: s.description }))
}

const PROFILE_COLORS: Record<string, string> = {
  we_ship_ltl: 'bg-blue-100 text-blue-800',
  mfr_ships_free_freight: 'bg-green-100 text-green-800',
  we_ship_small_parcel: 'bg-purple-100 text-purple-800',
  mfr_ships_ff_threshold: 'bg-emerald-100 text-emerald-800',
  mfr_ships_below_threshold: 'bg-orange-100 text-orange-800',
  mfr_ships_direct: 'bg-teal-100 text-teal-800',
}

export function E2ETestingPage() {
  const [seedResult, setSeedResult] = useState<SeedResult | null>(null)
  const [customerName, setCustomerName] = useState('Demo Restaurant')
  const [selectedScenarios, setSelectedScenarios] = useState<Record<string, string>>({})
  const [customerScenario, setCustomerScenario] = useState('')
  // Track which spec credit POs have had their PO sent (stores po_thread_uuid)
  const [sentPOs, setSentPOs] = useState<Record<string, string>>({})
  // Track which spec credit POs have had a reply injected
  const [scInjected, setScInjected] = useState<Record<string, boolean>>({})

  const { data: scenarios } = useE2EScenarios() as { data: E2EScenarios | undefined }
  const { data: status, refetch: refetchStatus } = useE2EStatus() as {
    data: (E2EStatus & { active_project?: SeedResult | null }) | undefined
    refetch: () => void
  }

  // Hydrate seed result from backend on first load (survives page refresh)
  /* eslint-disable react-hooks/set-state-in-effect -- one-time hydration from server state */
  useEffect(() => {
    if (!status?.active_project || seedResult) return
    const ap = status.active_project
    setSeedResult(ap)
    // Reconstruct sentPOs from POs that already have po_thread_uuid
    const sent: Record<string, string> = {}
    for (const po of ap.purchase_orders) {
      if (po.po_thread_uuid) sent[po.uuid] = po.po_thread_uuid
    }
    setSentPOs(sent)
    // Spec credit POs with po_thread_uuid have completed the SC flow
    const injected: Record<string, boolean> = {}
    for (const po of ap.purchase_orders) {
      if (po.has_spec_credit && po.po_thread_uuid) injected[po.uuid] = true
    }
    setScInjected(injected)
  }, [status?.active_project]) // eslint-disable-line react-hooks/exhaustive-deps
  /* eslint-enable react-hooks/set-state-in-effect */

  const seedMutation = useE2ESeed()
  const injectMutation = useE2EInjectReply()
  const injectSpecCreditMutation = useE2EInjectSpecCreditReply()
  const sendPOMutation = useE2ESendPO()
  const injectCustomerMutation = useE2EInjectCustomerReply()
  const injectShippingMutation = useE2EInjectShippingReply()
  const cleanupMutation = useE2ECleanup()

  const handleSeed = () => {
    seedMutation.mutate(
      { customer_name: customerName, manufacturer_count: 6, products_per_manufacturer: 2 },
      {
        onSuccess: (data) => {
          const result = data as unknown as SeedResult
          setSeedResult(result)
          setSentPOs({})
          setScInjected({})
          refetchStatus()
          const scCount = result.purchase_orders.filter((po) => po.has_spec_credit).length
          const directCount = result.purchase_orders.length - scCount
          toast.success(
            `Seeded ${result.purchase_orders.length} POs (${scCount} spec credit, ${directCount} direct) + customer thread`,
          )
        },
        onError: (err) => toast.error(`Seed failed: ${getApiErrorMessage(err)}`),
      },
    )
  }

  const handleInjectSpecCreditReply = (poUuid: string) => {
    const scenario = selectedScenarios[poUuid]
    if (!scenario) {
      toast.error('Select a scenario first')
      return
    }
    injectSpecCreditMutation.mutate(
      { purchase_order_uuid: poUuid, scenario_key: scenario },
      {
        onSuccess: () => {
          setScInjected((prev) => ({ ...prev, [poUuid]: true }))
          refetchStatus()
          toast.success(`Injected spec credit "${scenario}" — AI analysis queued`)
        },
        onError: (err) => toast.error(`Inject failed: ${getApiErrorMessage(err)}`),
      },
    )
  }

  const handleSendPO = (poUuid: string) => {
    sendPOMutation.mutate(
      { purchase_order_uuid: poUuid },
      {
        onSuccess: (data) => {
          const result = data as unknown as { po_thread_uuid: string; po_number: string }
          setSentPOs((prev) => ({ ...prev, [poUuid]: result.po_thread_uuid }))
          refetchStatus()
          toast.success(`PO ${result.po_number} sent — ready for manufacturer replies`)
        },
        onError: (err) => toast.error(`Send PO failed: ${getApiErrorMessage(err)}`),
      },
    )
  }

  const handleInjectReply = (poUuid: string) => {
    const scenario = selectedScenarios[`mfr-${poUuid}`]
    if (!scenario) {
      toast.error('Select a scenario first')
      return
    }
    injectMutation.mutate(
      { purchase_order_uuid: poUuid, scenario_key: scenario },
      {
        onSuccess: () => {
          refetchStatus()
          toast.success(`Injected "${scenario}" reply — AI analysis queued`)
        },
        onError: (err) => toast.error(`Inject failed: ${getApiErrorMessage(err)}`),
      },
    )
  }

  const handleInjectCustomerReply = () => {
    if (!seedResult?.customer_thread || !customerScenario) {
      toast.error('Seed a project and select a scenario first')
      return
    }
    injectCustomerMutation.mutate(
      { thread_uuid: seedResult.customer_thread.thread_uuid, scenario_key: customerScenario },
      {
        onSuccess: () => {
          refetchStatus()
          toast.success(`Injected customer "${customerScenario}" — AI analysis queued`)
        },
        onError: (err) => toast.error(`Inject failed: ${getApiErrorMessage(err)}`),
      },
    )
  }

  const handleInjectShippingReply = (poUuid: string) => {
    const scenario = selectedScenarios[`ship-${poUuid}`]
    if (!scenario) {
      toast.error('Select a scenario first')
      return
    }
    injectShippingMutation.mutate(
      { purchase_order_uuid: poUuid, scenario_key: scenario },
      {
        onSuccess: () => {
          refetchStatus()
          toast.success(`Injected carrier "${scenario}" reply — AI analysis queued`)
        },
        onError: (err) => toast.error(`Inject failed: ${getApiErrorMessage(err)}`),
      },
    )
  }

  const handleCleanup = () => {
    cleanupMutation.mutate(
      { project_uuid: seedResult?.project_uuid },
      {
        onSuccess: (data) => {
          setSeedResult(null)
          setSentPOs({})
          setScInjected({})
          refetchStatus()
          const result = data as unknown as { deleted: Record<string, number> }
          const deleted = result.deleted
          const total = deleted ? Object.values(deleted).reduce((a, b) => a + b, 0) : 0
          toast.success(`Cleaned up ${total} entities`)
        },
        onError: (err) => toast.error(`Cleanup failed: ${getApiErrorMessage(err)}`),
      },
    )
  }

  // Split POs into spec credit and direct
  const specCreditPOs = seedResult?.purchase_orders.filter((po) => po.has_spec_credit) ?? []
  const allPOs = seedResult?.purchase_orders ?? []

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex items-center gap-3">
        <FlaskConical className="h-6 w-6" />
        <h1 className="text-2xl font-bold tracking-tight">E2E Testing</h1>
      </div>

      {/* Status */}
      {status && (
        <div className="flex flex-wrap gap-3 text-sm">
          <Badge variant="outline">Projects: {status.projects}</Badge>
          <Badge variant="outline">POs: {status.purchase_orders}</Badge>
          <Badge variant="outline">Threads: {status.communication_threads}</Badge>
          <Badge variant="outline">Messages: {status.communication_messages}</Badge>
          <Badge variant="outline">Customer Threads: {status.customer_communication_threads}</Badge>
          <Badge variant="outline">Shipping Threads: {status.shipping_communication_threads}</Badge>
        </div>
      )}

      {/* Seed Section */}
      <Card>
        <CardHeader>
          <CardTitle>1. Seed Test Project</CardTitle>
          <CardDescription>
            Creates a project with 6 POs across different freight strategies.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-full sm:w-auto">
              <label className="mb-1 block text-sm font-medium">Customer Name</label>
              <InlineFieldInput
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="w-full sm:w-64"
              />
            </div>
            <LoadingButton
              loading={seedMutation.isPending}
              loadingText="Seeding..."
              onClick={handleSeed}
              disabled={!!seedResult}
            >
              <Play className="h-4 w-4" /> Seed Project
            </LoadingButton>
          </div>

          {seedResult && (
            <div className="space-y-3 rounded-md border p-4">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium">Project:</span>
                <Link
                  to="/projects/$projectId"
                  params={{ projectId: seedResult.project_uuid }}
                  className="text-blue-600 hover:underline"
                >
                  {seedResult.project_uuid.slice(0, 8)}...
                </Link>
              </div>
              <div className="space-y-1.5 text-sm">
                <span className="font-medium">Purchase Orders:</span>
                {seedResult.purchase_orders.map((po) => (
                  <div key={po.uuid} className="ml-4 flex flex-wrap items-center gap-2">
                    <Link
                      to="/purchase-orders/$poId"
                      params={{ poId: po.uuid }}
                      className="w-40 truncate font-mono text-blue-600 hover:underline"
                    >
                      {po.po_number}
                    </Link>
                    <span className="text-muted-foreground text-xs">{po.manufacturer_name}</span>
                    {po.shipping_label && (
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${PROFILE_COLORS[po.shipping_profile ?? ''] ?? 'bg-gray-100 text-gray-800'}`}
                      >
                        {po.shipping_label}
                      </span>
                    )}
                    {po.has_spec_credit ? (
                      <Badge variant="secondary" className="text-xs">
                        Spec Credit → PO
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">
                        PO Sent
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
              {seedResult.customer_thread && (
                <div className="text-sm">
                  <span className="font-medium">Customer Thread:</span>{' '}
                  <span className="font-mono text-xs">
                    {seedResult.customer_thread.thread_uuid.slice(0, 8)}...
                  </span>
                  <span className="text-muted-foreground ml-2">
                    to {seedResult.customer_thread.recipient_name}
                  </span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Spec Credit Phase */}
      {specCreditPOs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>2. Spec Credit Flow</CardTitle>
            <CardDescription>
              These POs are awaiting spec credit replies before the PO email is sent. Inject a spec
              credit reply, then click "Send PO" to simulate the PO email going out after credit
              resolution.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {specCreditPOs.map((po) => {
              const isSent = !!sentPOs[po.uuid]
              const isScInjected = !!scInjected[po.uuid]
              return (
                <div key={po.uuid} className="space-y-2 rounded-md border p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to="/purchase-orders/$poId"
                      params={{ poId: po.uuid }}
                      className="w-40 truncate font-mono text-sm text-blue-600 hover:underline"
                    >
                      {po.po_number}
                    </Link>
                    <span className="text-muted-foreground text-xs">{po.manufacturer_name}</span>
                    {po.shipping_label && (
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${PROFILE_COLORS[po.shipping_profile ?? ''] ?? 'bg-gray-100 text-gray-800'}`}
                      >
                        {po.shipping_label}
                      </span>
                    )}
                    {/* Status indicator */}
                    <div className="ml-auto flex items-center gap-1 text-xs">
                      <span
                        className={isScInjected ? 'text-green-600 font-medium' : 'text-amber-600'}
                      >
                        SC Reply
                      </span>
                      <ArrowRight className="h-3 w-3" />
                      <span className={isSent ? 'text-green-600 font-medium' : 'text-gray-400'}>
                        PO Sent
                      </span>
                    </div>
                  </div>

                  {/* Step 1: Inject spec credit reply */}
                  {!isSent && (
                    <div className="flex items-center gap-2">
                      <SearchSelect
                        className="w-full sm:w-80"
                        value={selectedScenarios[po.uuid] || undefined}
                        options={scenarioOptions(scenarios?.spec_credit)}
                        onChange={(next) =>
                          next && setSelectedScenarios((prev) => ({ ...prev, [po.uuid]: next }))
                        }
                        placeholder="Select spec credit scenario..."
                        searchPlaceholder="Search scenarios"
                      />
                      <LoadingButton
                        loading={
                          injectSpecCreditMutation.isPending &&
                          injectSpecCreditMutation.variables?.purchase_order_uuid === po.uuid
                        }
                        loadingText="Injecting..."
                        size="sm"
                        onClick={() => handleInjectSpecCreditReply(po.uuid)}
                        disabled={!selectedScenarios[po.uuid]}
                      >
                        <Send className="h-3 w-3" /> Inject SC Reply
                      </LoadingButton>

                      {/* Step 2: Send PO (after spec credit reply) */}
                      {isScInjected && (
                        <LoadingButton
                          loading={
                            sendPOMutation.isPending &&
                            sendPOMutation.variables?.purchase_order_uuid === po.uuid
                          }
                          loadingText="Sending..."
                          size="sm"
                          variant="default"
                          onClick={() => handleSendPO(po.uuid)}
                        >
                          <ArrowRight className="h-3 w-3" /> Send PO
                        </LoadingButton>
                      )}
                    </div>
                  )}

                  {isSent && (
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <Check className="h-4 w-4" />
                      PO sent — now inject manufacturer replies in section 3
                    </div>
                  )}
                </div>
              )
            })}
          </CardContent>
        </Card>
      )}

      {/* Manufacturer Replies Section */}
      {allPOs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>3. Inject Manufacturer Replies</CardTitle>
            <CardDescription>
              Simulate inbound emails from manufacturers on POs that have been sent. Each injection
              queues real AI analysis. Choose scenarios appropriate for each PO's freight strategy.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {allPOs.map((po) => {
              const hasThread = !!po.po_thread_uuid || !!sentPOs[po.uuid]
              const isLoading =
                injectMutation.isPending &&
                injectMutation.variables?.purchase_order_uuid === po.uuid
              return (
                <div key={po.uuid} className="flex flex-wrap items-center gap-2">
                  <Link
                    to="/purchase-orders/$poId"
                    params={{ poId: po.uuid }}
                    className="w-40 truncate font-mono text-sm text-blue-600 hover:underline"
                  >
                    {po.po_number}
                  </Link>
                  {po.shipping_label && (
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${PROFILE_COLORS[po.shipping_profile ?? ''] ?? 'bg-gray-100 text-gray-800'}`}
                    >
                      {po.shipping_label}
                    </span>
                  )}
                  {po.has_spec_credit && (
                    <Badge
                      variant={hasThread ? 'secondary' : 'outline'}
                      className={`text-xs ${!hasThread ? 'text-amber-600 border-amber-300' : ''}`}
                    >
                      {hasThread ? 'SC applied' : 'Awaiting SC (section 2)'}
                    </Badge>
                  )}
                  {hasThread ? (
                    <>
                      <SearchSelect
                        className="w-full sm:w-80"
                        value={selectedScenarios[`mfr-${po.uuid}`] || undefined}
                        options={scenarioOptions(scenarios?.manufacturer)}
                        onChange={(next) =>
                          next &&
                          setSelectedScenarios((prev) => ({ ...prev, [`mfr-${po.uuid}`]: next }))
                        }
                        placeholder="Select manufacturer scenario..."
                        searchPlaceholder="Search scenarios"
                      />
                      <LoadingButton
                        loading={isLoading}
                        loadingText="Injecting..."
                        size="sm"
                        onClick={() => handleInjectReply(po.uuid)}
                        disabled={!selectedScenarios[`mfr-${po.uuid}`]}
                      >
                        <Send className="h-3 w-3" /> Inject
                      </LoadingButton>
                    </>
                  ) : (
                    <span className="text-muted-foreground text-xs">
                      Complete spec credit flow first
                    </span>
                  )}
                </div>
              )
            })}
          </CardContent>
        </Card>
      )}

      {/* Customer Comms Section */}
      {seedResult?.customer_thread && (
        <Card>
          <CardHeader>
            <CardTitle>4. Customer Communications</CardTitle>
            <CardDescription>
              Inject simulated customer replies on the auto-created thread. Each injection queues
              real AI analysis for categorization and action creation.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm">
              <span className="font-medium">Thread:</span>{' '}
              <span className="font-mono">
                {seedResult.customer_thread.thread_uuid.slice(0, 8)}...
              </span>
              <span className="text-muted-foreground ml-2">
                to {seedResult.customer_thread.recipient_name} (
                {seedResult.customer_thread.recipient_email})
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <SearchSelect
                className="w-full sm:w-80"
                value={customerScenario || undefined}
                options={scenarioOptions(scenarios?.customer)}
                onChange={(next) => next && setCustomerScenario(next)}
                placeholder="Select customer scenario..."
                searchPlaceholder="Search scenarios"
              />
              <LoadingButton
                loading={injectCustomerMutation.isPending}
                loadingText="Injecting..."
                size="sm"
                onClick={handleInjectCustomerReply}
                disabled={!customerScenario}
              >
                <Send className="h-3 w-3" /> Inject Reply
              </LoadingButton>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Shipping / Carrier Replies Section */}
      {allPOs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>5. Inject Carrier Replies</CardTitle>
            <CardDescription>
              Simulate inbound emails from carriers (pickup failures, delivery exceptions, rate
              disputes). Only available for POs that have a booked shipment. Each injection queues
              real AI analysis via the shipping comm pipeline.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {allPOs.map((po) => {
              const hasThread = !!po.po_thread_uuid || !!sentPOs[po.uuid]
              const isLoading =
                injectShippingMutation.isPending &&
                injectShippingMutation.variables?.purchase_order_uuid === po.uuid
              return (
                <div key={po.uuid} className="flex flex-wrap items-center gap-2">
                  <Link
                    to="/purchase-orders/$poId"
                    params={{ poId: po.uuid }}
                    className="w-40 truncate font-mono text-sm text-blue-600 hover:underline"
                  >
                    {po.po_number}
                  </Link>
                  {po.shipping_label && (
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${PROFILE_COLORS[po.shipping_profile ?? ''] ?? 'bg-gray-100 text-gray-800'}`}
                    >
                      {po.shipping_label}
                    </span>
                  )}
                  {hasThread ? (
                    <>
                      <SearchSelect
                        className="w-full sm:w-80"
                        value={selectedScenarios[`ship-${po.uuid}`] || undefined}
                        options={scenarioOptions(scenarios?.shipping)}
                        onChange={(next) =>
                          next &&
                          setSelectedScenarios((prev) => ({ ...prev, [`ship-${po.uuid}`]: next }))
                        }
                        placeholder="Select carrier scenario..."
                        searchPlaceholder="Search scenarios"
                      />
                      <LoadingButton
                        loading={isLoading}
                        loadingText="Injecting..."
                        size="sm"
                        onClick={() => handleInjectShippingReply(po.uuid)}
                        disabled={!selectedScenarios[`ship-${po.uuid}`]}
                      >
                        <Send className="h-3 w-3" /> Inject
                      </LoadingButton>
                    </>
                  ) : (
                    <span className="text-muted-foreground text-xs">
                      Needs a booked shipment — inject a mfr reply with pickup details first
                    </span>
                  )}
                </div>
              )
            })}
          </CardContent>
        </Card>
      )}

      <Separator />

      {/* Cleanup Section */}
      <Card>
        <CardHeader>
          <CardTitle>Cleanup</CardTitle>
          <CardDescription>Remove all E2E test data from the database.</CardDescription>
        </CardHeader>
        <CardContent>
          <LoadingButton
            loading={cleanupMutation.isPending}
            loadingText="Cleaning up..."
            variant="destructive"
            onClick={handleCleanup}
          >
            <Trash2 className="h-4 w-4" /> Clean Up All Test Data
          </LoadingButton>
        </CardContent>
      </Card>
    </div>
  )
}
